import React from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Divider,
  Link,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import { Theme, useTheme } from '@mui/material/styles';
import { lowerCase, startCase } from 'lodash';
import { useRouter } from 'next/router';
import { NavLinkInterface } from 'server/config-cache/types';
import UserContext from 'src/context/user';
import useNonNullContext from 'src/hooks/useNonNullContext';

interface Props {
  topLinks: NavLinkInterface[];
  accountLinks: NavLinkInterface[];
  bottomLinks: NavLinkInterface[];
  logout: () => Promise<void>;
}

const Index: React.FC<Props> = ({
  accountLinks,
  bottomLinks,
  logout,
  topLinks,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);

  const { user } = useNonNullContext(UserContext);

  const router = useRouter();

  const handleMenu: React.MouseEventHandler<HTMLDivElement> = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const toggleMenu: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  // can't render if no user; return null.
  if (user === null) {
    return null;
  }

  // non null is safe b/c we check just above and return if null.

  const { email, initials } = user!;

  return (
    <Box
      sx={{ display: 'flex', marginLeft: '1.5rem' }}
      onClick={isSmallScreen ? toggleMenu : undefined}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center' }}
        onMouseOver={isSmallScreen ? undefined : handleMenu}
      >
        <Avatar
          aria-label="account of current user"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          sx={{
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.common.white,
            width: '2rem',
            height: '2rem',
            marginRight: '0.25rem',
          }}
          component={'div'}
        >
          {initials}
        </Avatar>
        <ArrowDropDownIcon
          sx={{
            color: theme.palette.text.secondary,
            transition: 'transform 0.2s',
            transform: anchorEl ? 'rotate(180deg)' : null,
          }}
        />
      </Box>
      <Menu
        disableAutoFocusItem
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        MenuListProps={{
          onMouseLeave: handleClose,
          sx: { padding: 0, minWidth: '16.5rem' },
        }}
      >
        {topLinks.map((l) => (
          <Link
            key={`${l.url}-${l.displayText}`}
            href={l.url}
            sx={{ textDecoration: 'none' }}
          >
            <MenuItem
              selected={router.pathname === l.url}
              sx={{
                padding: '0.625rem 1.875rem',
                color: theme.palette.text.primary,
                '&.Mui-selected': {
                  fontWeight: 'bold',
                },
              }}
            >
              {startCase(lowerCase(l.displayText))}
            </MenuItem>
          </Link>
        ))}

        {!!topLinks.length && <Divider sx={{ margin: 0 }} />}

        {(!!topLinks.length || !!bottomLinks.length) && (
          <MenuItem sx={{ padding: '0.625rem 1.875rem' }}>
            <Typography variant={'overline'}>Account</Typography>
          </MenuItem>
        )}
        {accountLinks.map((l) => (
          <Link
            key={`${l.url}-${l.displayText}`}
            href={l.url}
            sx={{ textDecoration: 'none' }}
          >
            <MenuItem
              sx={{
                padding: '0.625rem 1.875rem',
                color: theme.palette.text.primary,
              }}
            >
              {startCase(lowerCase(l.displayText))}
            </MenuItem>
          </Link>
        ))}
        <MenuItem
          sx={{ padding: '1rem 1.875rem', display: 'block' }}
          onClick={logout}
        >
          <Link underline="hover">
            <Typography
              variant="body1"
              component="div"
              color={theme.palette.text.primary}
            >
              Sign Out
            </Typography>
          </Link>
          <Typography variant="caption" color={theme.palette.text.disabled}>
            {email}
          </Typography>
        </MenuItem>

        {!!bottomLinks.length && <Divider sx={{ margin: '0 !important' }} />}

        {bottomLinks.map((l) => (
          <Link
            key={`${l.url}-${l.displayText}`}
            href={l.url}
            sx={{ textDecoration: 'none' }}
          >
            <MenuItem
              sx={{
                padding: '0.625rem 1.875rem',
                color: theme.palette.text.primary,
              }}
            >
              {startCase(lowerCase(l.displayText))}
            </MenuItem>
          </Link>
        ))}
      </Menu>
    </Box>
  );
};

export default Index;
